import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import Duration from './Duration';
import ServiceCost from './ServiceCost';
import dataAids from '../constants/dataAids';
import { Translate as translate } from '@wsb/vnext-widget-content-translate';
import FormattersStore from '../stores/Formatters';
import TrackImpression from '../../common/components/TrackImpression';
import { costTypes } from '../constants/costTypes';
import {
  SERVICE_PAY_LATER_LABEL_IMPRESSION,
  SERVICE_DEPOSIT_REQUIRED_LABEL_IMPRESSION,
  SERVICE_NO_PAYMENT_LABEL_IMPRESSION
} from '../constants/metrics';
import { trackMetric } from '../utils/trackMetric';
import { getCostTextWithAddons, isFreeServiceAndHasPaidAddons } from '../utils/getCostInfo';
import AvailableTimeSelectionStore from '../stores/AvailableTimeSelection';
import AddonInfo from './AddonInfo';

const styles = {
  durationAndCostContainer: {
    display: 'flex',
    alignItems: 'baseline'
  },
  payLaterLabel: {
    color: 'section',
    fontSize: 'small',
    display: 'inline-block'
  },
  depositRequiredLabel: {
    color: 'section',
    fontSize: 'small',
    display: 'block',
    marginTop: 'xsmall'
  },
  serviceCost: {
    marginRight: 'xsmall'
  },
  addonsPriceContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: 'xsmall',
    gap: 'xsmall'
  },
  subtotalText: {
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  },
  addOnsListDepositRequired: {
    fontSize: '14px',
    fontWeight: 400,
    color: 'black'
  },
  addOnsListPayLater: {
    fontSize: '14px',
    fontWeight: 400,
    color: 'black',
    marginLeft: 'xsmall'
  }
};

export default function DurationAndCost({
  service,
  depositShown,
  withPaymentLabels,
  staticContent,
  isOlaStrikeThroughPriceExperimentEnabled,
  showAddonsInfo = false,
  as: T = UX2.Element.Text,
  ...props
}) {
  const { Element } = UX2.Element;
  const { Block } = UX2.Element;
  const { Text } = UX2.Element;
  const interpolate = translate(staticContent);
  const { numberFormatter } = FormattersStore.getState();
  const { selectedAddons } = AvailableTimeSelectionStore.getState();

  const isPayLaterLabelShown = () => {
    if (!withPaymentLabels) return false;
    if (service.cost_type === costTypes.FREE) return false;
    if (service.cost_type === costTypes.CUSTOM_TEXT) return false;
    if (service.payment_option !== 'dont_charge_upfront') return false;
    return true;
  };

  const isDepositRequiredLabelShown = () => {
    if (!withPaymentLabels) return false;
    if (!depositShown) return false;
    if (service.payment_option !== 'charge_deposit') return false;
    return true;
  };

  const isPaymentLabelShown = () => {
    if (isPayLaterLabelShown()) return true;
    if (isDepositRequiredLabelShown()) return true;
    return false;
  };

  const isAddonCostShown = () => {
    if (!showAddonsInfo) return false;
    if (selectedAddons.length === 0) return false;
    return true;
  };

  useEffect(() => {
    if (withPaymentLabels && !isPaymentLabelShown()) {
      trackMetric(SERVICE_NO_PAYMENT_LABEL_IMPRESSION);
    }
  }, []);

  if (!isAddonCostShown()) {
    return (
      <T { ...props } style={ styles.durationAndCostContainer }>
        <Duration duration={ service.duration } staticContent={ staticContent } />
        <Element
          data-aid={ dataAids.DURATION_AND_COST_SEPARATOR }
          style={{ marginHorizontal: 'xsmall' }}
        >
          |
        </Element>
        <ServiceCost
          service={ service }
          staticContent={ staticContent }
          styles={ styles.serviceCost }
          isOlaStrikeThroughPriceExperimentEnabled={ isOlaStrikeThroughPriceExperimentEnabled }
        />
        { isPayLaterLabelShown() && (
          <TrackImpression metric={ SERVICE_PAY_LATER_LABEL_IMPRESSION }>
            <Element data-aid={ dataAids.SERVICE_INFO_PAY_LATER_LABEL } style={ styles.payLaterLabel }>
              { staticContent.payLater }
            </Element>
          </TrackImpression>
        ) }
        { isDepositRequiredLabelShown() && (
          <TrackImpression metric={ SERVICE_DEPOSIT_REQUIRED_LABEL_IMPRESSION }>
            <Element
              data-aid={ dataAids.SERVICE_INFO_DEPOSIT_REQUIRED_LABEL }
              style={ styles.depositRequiredLabel }
            >
              { interpolate('depositRequired', {
                amount: numberFormatter.formatCurrency(+service.deposit_amount)
              }) }
            </Element>
          </TrackImpression>
        ) }
      </T>
    );
  }

  const totalCost = getCostTextWithAddons(
    service,
    selectedAddons,
    isOlaStrikeThroughPriceExperimentEnabled
  );

  return (
    <Block>
      <T { ...props } style={ styles.durationAndCostContainer }>
        <ServiceCost
          service={ service }
          staticContent={ staticContent }
          styles={ styles.serviceCost }
          isOlaStrikeThroughPriceExperimentEnabled={ isOlaStrikeThroughPriceExperimentEnabled }
        />
        <Element
          data-aid={ dataAids.DURATION_AND_COST_SEPARATOR }
          style={{ marginHorizontal: 'xsmall' }}
        >
          |
        </Element>
        <Duration duration={ service.duration } staticContent={ staticContent } />
      </T>
      <AddonInfo selectedAddons={ selectedAddons } />
      <Block style={ styles.addonsPriceContainer }>
        <Text style={ styles.subtotalText }>
          { staticContent.subtotal } - { totalCost }
          { (isPayLaterLabelShown() || isFreeServiceAndHasPaidAddons(service, selectedAddons)) && (
            <TrackImpression metric={ SERVICE_PAY_LATER_LABEL_IMPRESSION }>
              <Element
                data-aid={ dataAids.SERVICE_INFO_PAY_LATER_LABEL }
                style={ styles.addOnsListPayLater }
              >
                { staticContent.payLater }
              </Element>
            </TrackImpression>
          ) }
        </Text>

        { isDepositRequiredLabelShown() && (
          <TrackImpression metric={ SERVICE_DEPOSIT_REQUIRED_LABEL_IMPRESSION }>
            <Element
              data-aid={ dataAids.SERVICE_INFO_DEPOSIT_REQUIRED_LABEL }
              style={ styles.addOnsListDepositRequired }
            >
              { interpolate('depositRequired', {
                amount: numberFormatter.formatCurrency(+service.deposit_amount)
              }) }
            </Element>
          </TrackImpression>
        ) }
      </Block>
    </Block>
  );
}

DurationAndCost.propTypes = {
  service: PropTypes.object,
  depositShown: PropTypes.bool,
  withPaymentLabels: PropTypes.bool,
  staticContent: PropTypes.object.isRequired,
  isOlaStrikeThroughPriceExperimentEnabled: PropTypes.bool,
  showAddonsInfo: PropTypes.bool,
  as: PropTypes.any
};

DurationAndCost.defaultProps = {
  withPaymentLabels: true
};
